import React, { useState } from 'react';
import { useProjectList } from './hooks/useProjectList';
import ProjectToolbar from './components/ProjectToolbar';
import ProjectTable from './components/ProjectTable';
import ProjectArchive from './components/ProjectArchive';
import ProjectTrash from './components/ProjectTrash';
import ProjectDetailModal from '../ProjectModal/ProjectDetailModal';
import ProjectOverviewMap from '../ProjectOverviewMap';
import BulkActionBar from '../../shared/BulkActionBar';
import { STATUS_OPTIONS } from './constants';
import './ProjectList.css';

/**
 * ProjectList orchestrator component.
 * Manages the layout and connects the hook with the sub-components.
 * 
 * @param {Object} props
 * @param {string} props.variant - The variant of the list ('admin' or 'tool').
 * @param {Function} props.onProjectSelect - Callback when a project is selected.
 */
export default function ProjectList({ variant = 'admin', onProjectSelect }) {
    const {
        projects,
        loading,
        viewMode,
        searchTerm,
        statusFilter,
        isFilterOpen,
        filterRef,
        sortConfig,
        isArchiveOpen,
        isListOpen,
        archiveSearch,
        focusedProject,
        globalPermissions,
        showProjectModal,
        projectMode,
        projectForm,
        selectedProject,
        selectedProjectIds,
        activeList,
        archivedList,
        allFilteredProjects,
        setSearchTerm,
        setStatusFilter,
        setIsFilterOpen,
        setViewMode,
        setIsArchiveOpen,
        setIsListOpen,
        setArchiveSearch,
        setFocusedProject,
        setSelectedProjectIds,
        setShowProjectModal,
        setSelectedProject,
        setProjectMode,
        handleSort,
        toggleStatusFilter,
        handleSelectAllFilters,
        handleSelectNoneFilters,
        getFilterLabel,
        openCreateProject,
        openViewProject,
        handleDeleteProject,
        handleRestoreProject,
        handleBulkRestore,
        handleHardDelete,
        handleBulkHardDelete,
        handleBulkStatusChange,
        handleBulkDelete,
        handleProjectSubmit,
        isTrashOpen,
        setIsTrashOpen,
        trashSearch,
        setTrashSearch,
        trashList,
        deletedProjects,
        activeSort,
        archiveSort,
        trashSort,
        handleSortActive,
        handleSortArchive,
        handleSortTrash
    } = useProjectList({ variant, onProjectSelect });

    const storedUser = localStorage.getItem('user');
    const currentUser = storedUser ? JSON.parse(storedUser) : null;



    return (
        <div className="project-list-container">
            <ProjectToolbar 
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                isFilterOpen={isFilterOpen}
                setIsFilterOpen={setIsFilterOpen}
                getFilterLabel={getFilterLabel}
                statusFilter={statusFilter}
                toggleStatusFilter={toggleStatusFilter}
                handleSelectAllFilters={handleSelectAllFilters}
                handleSelectNoneFilters={handleSelectNoneFilters}
                viewMode={viewMode}
                setViewMode={setViewMode}
                globalPermissions={globalPermissions}
                openCreateProject={openCreateProject}
                filterRef={filterRef}
                isTrashOpen={isTrashOpen}
                setIsTrashOpen={setIsTrashOpen}
                trashCount={trashList.length}
            />

            {loading ? (
                viewMode === 'list' ? (
                    <div className="projects-table-wrapper" style={{ maxWidth: '1200px', margin: '0 auto' }}>
                        <table className="projects-table">
                            <thead>
                                <tr>
                                    <th style={{ width: '100px' }}>Status</th>
                                    <th style={{ width: '80px' }}>Nr.</th>
                                    <th>Name</th>
                                    <th>Standort</th>
                                    <th style={{ width: '80px' }}>Aktionen</th>
                                </tr>
                            </thead>
                            <tbody>
                                {[...Array(5)].map((_, i) => (
                                    <tr key={i}>
                                        <td><div className="skeleton skeleton-pill" style={{ margin: '0 auto' }}></div></td>
                                        <td><div className="skeleton skeleton-text short" style={{ margin: '0 auto', display: 'block' }}></div></td>
                                        <td><div className="skeleton skeleton-text medium"></div></td>
                                        <td><div className="skeleton skeleton-text long"></div></td>
                                        <td><div className="skeleton skeleton-text short" style={{ margin: '0 auto', display: 'block' }}></div></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                        <div className="skeleton-map">
                            <div className="skeleton skeleton-text long" style={{ height: '20px', width: '200px' }}></div>
                        </div>
                    </div>
                )
            ) : viewMode === 'list' ? (
                <>
                    <div className="archive-divider" style={{ maxWidth: '1200px', margin: '15px auto 0 auto' }}>
                        <button className="archive-toggle-btn" onClick={() => setIsListOpen(!isListOpen)}>
                            {isListOpen ? '🔼 Liste ausblenden' : `🔽 Liste anzeigen (${activeList.length})`}
                        </button>
                    </div>
                    {isListOpen && (
                        <>
                            {selectedProjectIds.length > 0 && selectedProjectIds.some(id => activeList.some(p => p.id === id)) && (
                                <div style={{ maxWidth: '1200px', margin: '10px auto', padding: '0 16px', boxSizing: 'border-box' }}>
                                    <BulkActionBar 
                                        selectedCount={selectedProjectIds.filter(id => activeList.some(p => p.id === id)).length}
                                        onClearSelection={() => setSelectedProjectIds(prev => prev.filter(id => !activeList.some(p => p.id === id)))}
                                        hideSelectionText={true}
                                        actions={[
                                            globalPermissions.delete_projects && {
                                                label: `${selectedProjectIds.filter(id => activeList.some(p => p.id === id)).length} Löschen`,
                                                icon: '🗑️',
                                                variant: 'danger',
                                                onClick: handleBulkDelete
                                            },
                                            {
                                                render: () => (
                                                    <div style={{ position: 'relative', minWidth: '160px' }}>
                                                        <div 
                                                            className="btn-bulk-action secondary" 
                                                            style={{ 
                                                                height: '34px', 
                                                                display: 'flex', 
                                                                alignItems: 'center', 
                                                                justifyContent: 'space-between',
                                                                padding: '0 12px',
                                                                pointerEvents: 'none',
                                                                boxSizing: 'border-box'
                                                            }}
                                                        >
                                                            <span style={{ fontSize: '13px', fontWeight: '500' }}>Status ändern...</span>
                                                            <span style={{ fontSize: '10px', color: '#64748b' }}>▼</span>
                                                        </div>
                                                        <select 
                                                            key="status-select"
                                                            onChange={(e) => e.target.value && handleBulkStatusChange(e.target.value)} 
                                                            style={{ 
                                                                position: 'absolute', 
                                                                top: 0, 
                                                                left: 0, 
                                                                width: '100%', 
                                                                height: '100%', 
                                                                opacity: 0, 
                                                                cursor: 'pointer' 
                                                            }}
                                                        >
                                                            <option value="">Status ändern...</option>
                                                            {STATUS_OPTIONS.filter(opt => opt.value !== 'deleted').map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                                        </select>
                                                    </div>
                                                )
                                            }
                                        ].filter(Boolean)}
                                    />
                                </div>
                            )}
                            <ProjectTable 
                                list={activeList}
                                showBulk={true}
                                globalPermissions={globalPermissions}
                                selectedProjectIds={selectedProjectIds}
                                setSelectedProjectIds={setSelectedProjectIds}
                                handleSort={handleSortActive}
                                sortConfig={activeSort}
                                onProjectSelect={onProjectSelect}
                                setSelectedProject={setSelectedProject}
                                setProjectMode={setProjectMode}
                                setShowProjectModal={setShowProjectModal}
                                setFocusedProject={setFocusedProject}
                                setViewMode={setViewMode}
                                handleDeleteProject={handleDeleteProject}
                                variant={variant}
                            />
                        </>
                    )}
                </>
            ) : (
                <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                    <ProjectOverviewMap
                        projects={allFilteredProjects}
                        onProjectSelect={onProjectSelect}
                        onProjectEdit={(p) => openViewProject(p, (p.can_edit_project || globalPermissions.edit_projects) ? 'edit' : 'view')}
                        focusedProject={focusedProject}
                        onFocusCleared={() => setFocusedProject(null)}
                    />
                </div>
            )}

            <ProjectArchive 
                viewMode={viewMode}
                isArchiveOpen={isArchiveOpen}
                setIsArchiveOpen={setIsArchiveOpen}
                archiveSearch={archiveSearch}
                setArchiveSearch={setArchiveSearch}
                selectedProjectIds={selectedProjectIds}
                setSelectedProjectIds={setSelectedProjectIds}
                archivedList={archivedList}
                handleBulkStatusChange={handleBulkStatusChange}
                handleBulkDelete={handleBulkDelete}
                globalPermissions={globalPermissions}
                projects={projects}
                tableProps={{
                    selectedProjectIds,
                    setSelectedProjectIds,
                    handleSort: handleSortArchive,
                    sortConfig: archiveSort,
                    onProjectSelect,
                    setSelectedProject,
                    setProjectMode,
                    setShowProjectModal,
                    setFocusedProject,
                    setViewMode,
                    handleDeleteProject,
                    variant
                }}
            />

            <ProjectTrash
                viewMode={viewMode}
                isTrashOpen={isTrashOpen}
                setIsTrashOpen={setIsTrashOpen}
                trashSearch={trashSearch}
                setTrashSearch={setTrashSearch}
                trashList={trashList}
                handleRestoreProject={handleRestoreProject}
                handleBulkRestore={handleBulkRestore}
                handleHardDelete={handleHardDelete}
                handleBulkHardDelete={handleBulkHardDelete}
                globalPermissions={globalPermissions}
                tableProps={{
                    selectedProjectIds,
                    setSelectedProjectIds,
                    handleSort: handleSortTrash,
                    sortConfig: trashSort,
                    onProjectSelect,
                    setSelectedProject,
                    setProjectMode,
                    setShowProjectModal,
                    setFocusedProject,
                    setViewMode,
                    handleDeleteProject,
                    variant
                }}
            />

            <ProjectDetailModal
                isOpen={showProjectModal}
                onClose={() => setShowProjectModal(false)}
                project={selectedProject || projectForm}
                mode={projectMode}
                onSubmit={handleProjectSubmit}
                onDelete={handleDeleteProject}
                projects={projects}
                deletedProjects={deletedProjects}
                currentUser={currentUser}
            />
        </div>
    );
}
